import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigateSearchResult } from '../../hooks/useNavigateSearchResult';
import {
  mergeSearchResults,
  searchDocuments,
  searchPages,
  type GlobalSearchResult,
} from '../../lib/globalSearch';
import { portalKindFromPath } from '../../lib/portal';

type FlatRow = {
  key: string;
  result: GlobalSearchResult;
  group: 'Pages' | 'Documents';
};

export function GlobalSearch() {
  const { portalAccess } = useAuth();
  const navigateResult = useNavigateSearchResult();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [docs, setDocs] = useState<GlobalSearchResult[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const pageHits = useMemo(() => {
    if (!portalAccess || !query.trim()) return [];
    return searchPages(portalAccess, query);
  }, [portalAccess, query]);

  const merged = useMemo(() => mergeSearchResults(pageHits, docs), [pageHits, docs]);

  const flatRows: FlatRow[] = useMemo(() => {
    const rows: FlatRow[] = [];
    for (const result of merged.pages) {
      rows.push({ key: result.id, result, group: 'Pages' });
    }
    for (const result of merged.documents) {
      rows.push({ key: result.id, result, group: 'Documents' });
    }
    return rows;
  }, [merged]);

  useEffect(() => {
    if (!portalAccess) return;
    const q = query.trim();
    if (q.length < 2) {
      setDocs([]);
      setDocsError(null);
      setDocsLoading(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setDocsLoading(true);
      setDocsError(null);
      void searchDocuments(portalAccess, q)
        .then((rows) => {
          if (!cancelled) setDocs(rows);
        })
        .catch((e) => {
          if (!cancelled) {
            setDocs([]);
            setDocsError(e instanceof Error ? e.message : 'Search failed.');
          }
        })
        .finally(() => {
          if (!cancelled) setDocsLoading(false);
        });
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [portalAccess, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, flatRows.length]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    function onKey(event: globalThis.KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const selectResult = async (result: GlobalSearchResult) => {
    setOpen(false);
    setQuery('');
    setDocs([]);
    await navigateResult(result);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!flatRows.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(flatRows.length - 1, i + 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const row = flatRows[activeIndex];
      if (row) void selectResult(row.result);
    }
  };

  if (!portalAccess) return null;

  const showPanel = open && query.trim().length > 0;
  const currentPortal = portalKindFromPath(window.location.pathname);
  const empty =
    !docsLoading &&
    query.trim().length > 0 &&
    flatRows.length === 0 &&
    !docsError;

  let lastGroup: string | null = null;

  return (
    <div className="global-search" ref={rootRef}>
      <label className="global-search__field">
        <span className="global-search__icon" aria-hidden>
          ⌕
        </span>
        <input
          ref={inputRef}
          type="search"
          className="global-search__input"
          placeholder="Search pages & documents…"
          value={query}
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        <kbd className="global-search__hint" title="Keyboard shortcut">
          Ctrl K
        </kbd>
      </label>

      {showPanel ? (
        <div className="global-search__panel" role="listbox" id={listId}>
          {docsError ? <p className="global-search__status global-search__status--error">{docsError}</p> : null}
          {docsLoading ? <p className="global-search__status">Searching documents…</p> : null}
          {empty ? (
            <p className="global-search__status">
              {query.trim().length < 2
                ? 'Type at least 2 characters to search documents.'
                : `No matches in pages or documents you can access${
                    currentPortal ? ` (from ${currentPortal.toUpperCase()})` : ''
                  }.`}
            </p>
          ) : null}

          {flatRows.map((row, index) => {
            const showGroup = row.group !== lastGroup;
            lastGroup = row.group;
            return (
              <div key={row.key}>
                {showGroup ? <div className="global-search__group">{row.group}</div> : null}
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`global-search__item${index === activeIndex ? ' is-active' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => void selectResult(row.result)}
                >
                  <span className="global-search__item-title">{row.result.title}</span>
                  {row.result.subtitle ? (
                    <span className="global-search__item-sub">{row.result.subtitle}</span>
                  ) : null}
                  <span className="global-search__item-loc">{row.result.location}</span>
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
